/**
 * 随机奇遇事件数据
 */

export interface EventEffect {
  type: 'exp' | 'stones' | 'hp' | 'mp' | 'item' | 'damage' | 'buff_exp' | 'buff_breakthrough';
  value: number;             // 数值（正=增益，负=减益）
  itemId?: string;           // type=item 时使用
  duration?: number;         // buff 持续次数（仅 buff 类）
  message: string;           // 效果描述
}

export interface EventChoice {
  text: string;
  effects: EventEffect[];
}

export interface GameEvent {
  id: string;
  title: string;
  desc: string;
  /** 触发场景 */
  trigger: 'cultivate' | 'combat_end' | 'idle' | 'tick_rare' | 'breakthrough';
  /** 每次触发的概率 */
  chance: number;
  choices: EventChoice[];
}

export const EVENTS: GameEvent[] = [
  // ===== 修炼时触发 =====
  {
    id: 'sudden_insight',
    title: '灵光一闪',
    desc: '修炼中你突然进入一种玄妙的顿悟状态，天地灵气疯狂涌入体内。',
    trigger: 'cultivate',
    chance: 0.02,
    choices: [
      {
        text: '顺势修炼',
        effects: [
          { type: 'exp', value: 200, message: '获得 200 点修炼经验' },
        ],
      },
      {
        text: '尝试冲击瓶颈',
        effects: [
          { type: 'exp', value: 100, message: '获得 100 点修炼经验' },
          { type: 'buff_breakthrough', value: 0.1, duration: 1, message: '下次突破成功率+10%' },
        ],
      },
    ],
  },
  {
    id: 'inner_demon',
    title: '心魔入侵',
    desc: '修炼中一道黑影在识海中浮现，是你内心深处最深的恐惧所化。',
    trigger: 'cultivate',
    chance: 0.01,
    choices: [
      {
        text: '以意志对抗',
        effects: [
          { type: 'mp', value: -20, message: '消耗大量灵力对抗心魔（MP-20）' },
          { type: 'exp', value: 80, message: '战胜心魔，获得 80 点修炼经验' },
        ],
      },
      {
        text: '暂时回避',
        effects: [
          { type: 'hp', value: -30, message: '心魔反噬（HP-30）' },
        ],
      },
    ],
  },

  // ===== 战斗胜利后触发 =====
  {
    id: 'wandering_merchant',
    title: '游方商人',
    desc: '战斗结束后，一位游方商人从远处走来，表示想用丹药换取你刚获得的战利品。',
    trigger: 'combat_end',
    chance: 0.15,
    choices: [
      {
        text: '交换丹药',
        effects: [
          { type: 'stones', value: -30, message: '花费 30 灵石' },
          { type: 'item', value: 1, itemId: 'exp_pill', message: '获得悟道丹×1' },
        ],
      },
      {
        text: '婉拒',
        effects: [
          { type: 'stones', value: 20, message: '商人留下 20 灵石作为见面礼' },
        ],
      },
    ],
  },
  {
    id: 'injured_senior',
    title: '受伤的前辈',
    desc: '前方一位负伤的年长修士倒地不起，看见你后低声求助。',
    trigger: 'combat_end',
    chance: 0.1,
    choices: [
      {
        text: '上前救助',
        effects: [
          { type: 'mp', value: -20, message: '你消耗灵力为其疗伤（MP-20）' },
          { type: 'stones', value: 100, message: '前辈康复后赠予 100 灵石作为答谢' },
        ],
      },
      {
        text: '冷眼旁观',
        effects: [],
      },
      {
        text: '趁人之危',
        effects: [
          { type: 'stones', value: 80, message: '搜刮了 80 灵石（你的道心蒙上了一层阴影）' },
          { type: 'exp', value: -50, message: '邪念滋生，修炼受阻（经验-50）' },
        ],
      },
    ],
  },
  {
    id: 'hidden_cave',
    title: '前人洞府',
    desc: '战斗的余波炸开了一处隐蔽的山壁，露出一个古老的洞府入口。',
    trigger: 'combat_end',
    chance: 0.08,
    choices: [
      {
        text: '进去探索',
        effects: [
          { type: 'exp', value: 150, message: '发现前人遗留的修炼心得（+150经验）' },
          { type: 'stones', value: 120, message: '发现 120 灵石' },
        ],
      },
      {
        text: '谨慎行事，只在外围查看',
        effects: [
          { type: 'stones', value: 50, message: '在外围找到 50 灵石' },
        ],
      },
    ],
  },

  // ===== 离线归来触发 =====
  {
    id: 'spirit_herb_found',
    title: '意外收获',
    desc: '挂机期间，你的角色在山中偶然发现了一株灵草。',
    trigger: 'idle',
    chance: 0.4,
    choices: [
      {
        text: '采摘炼化',
        effects: [
          { type: 'exp', value: 100, message: '炼化灵草，获得 100 点修炼经验' },
        ],
      },
      {
        text: '保存备用',
        effects: [
          { type: 'item', value: 1, itemId: 'spirit_herb', message: '获得灵草×1' },
        ],
      },
    ],
  },
  {
    id: 'strange_dream',
    title: '奇异的梦',
    desc: '你做了一个奇怪的梦，梦中一位白发老者向你传授了一段口诀。',
    trigger: 'idle',
    chance: 0.25,
    choices: [
      {
        text: '铭记口诀',
        effects: [
          { type: 'exp', value: 250, message: '梦中悟道，获得 250 点修炼经验' },
        ],
      },
      {
        text: '觉得不祥，置之不理',
        effects: [],
      },
    ],
  },
  {
    id: 'thief_visited',
    title: '不速之客',
    desc: '你离开期间，有人翻动了你的行囊。还好大部分财物都随身携带。',
    trigger: 'idle',
    chance: 0.15,
    choices: [
      {
        text: '检查损失',
        effects: [
          { type: 'stones', value: -30, message: '被偷走 30 灵石' },
        ],
      },
    ],
  },

  // ===== 极低概率 tick 事件 =====
  {
    id: 'celestial_phenomenon',
    title: '天降异象',
    desc: '天空突然出现七彩祥云，方圆百里的灵气浓度骤增！',
    trigger: 'tick_rare',
    chance: 0.002,  // 平均约8分钟触发一次
    choices: [
      {
        text: '立刻修炼吸收',
        effects: [
          { type: 'exp', value: 500, message: '吸收天地灵气，获得 500 点修炼经验！' },
        ],
      },
    ],
  },
  {
    id: 'earth_tremor',
    title: '地脉震动',
    desc: '脚下大地突然剧烈震动，一道裂缝中涌出浓郁的地脉灵气。',
    trigger: 'tick_rare',
    chance: 0.003,
    choices: [
      {
        text: '靠近吸收',
        effects: [
          { type: 'exp', value: 200, message: '吸收地脉灵气，获得 200 点修炼经验' },
          { type: 'hp', value: -15, message: '被碎石击中（HP-15）' },
        ],
      },
      {
        text: '远离危险',
        effects: [],
      },
    ],
  },
];
